"use client";

import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookOpen, FileText, Layers, Package, Sparkles } from "lucide-react";
import type { GameSearchResult } from "@/lib/game-data/types";
import { GAME_SEARCH_CATEGORY_LABELS } from "@/lib/game-data/types";
import type {
  HighlightSearch,
  PageSearch,
  SiteCategorySearch,
  WikiSearchPost,
} from "@/data/searchdata";

export type SearchFilterBucket = {
  wikiPosts: WikiSearchPost[];
  siteCategories: SiteCategorySearch[];
  highlights: HighlightSearch[];
  pages: PageSearch[];
};

type Props = {
  searchQuery: string;
  /** Khi đúng và query rỗng = hiển thị gợi ý nhanh */
  mode: "suggestions" | "filtered" | "empty";
  filtered: SearchFilterBucket;
  suggested: SearchFilterBucket;
  gameResults?: GameSearchResult[];
  isGameLoading?: boolean;
  onNavigate: () => void;
  onViewAll?: () => void;
  onViewGameSearch?: () => void;
};

export function HeaderSearchResults({
  searchQuery,
  mode,
  filtered,
  suggested,
  gameResults = [],
  isGameLoading = false,
  onNavigate,
  onViewAll,
  onViewGameSearch,
}: Props) {
  const data = mode === "suggestions" ? suggested : mode === "filtered" ? filtered : { wikiPosts: [], siteCategories: [], highlights: [], pages: [] };
  const trimmed = searchQuery.trim();
  const showGameFooter = Boolean(trimmed && onViewGameSearch);

  const gameFooter = showGameFooter ? (
    <div className="sticky bottom-0 border-t bg-background/95 px-2 pb-1 pt-2 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <Button type="button" variant="default" className="w-full justify-center gap-2" onClick={onViewGameSearch}>
        {isGameLoading ? "Đang tìm…" : "Xem đầy đủ kết quả tra cứu"}
      </Button>
      {onViewAll ? (
        <Button type="button" variant="outline" className="mt-2 w-full justify-center gap-2" onClick={onViewAll}>
          Xem thêm wiki & trang web
        </Button>
      ) : null}
    </div>
  ) : null;

  if (mode === "empty" && gameResults.length === 0 && !isGameLoading) {
    return (
      <div>
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          <p>Không có kết quả nhanh cho «{searchQuery}».</p>
          <p className="mt-1 text-xs">Nhấn Enter hoặc nút bên dưới để mở trang tra cứu đầy đủ.</p>
        </div>
        {gameFooter}
      </div>
    );
  }

  return (
    <div className="max-h-[min(70vh,520px)] overflow-y-auto pb-2">
      {(isGameLoading || gameResults.length > 0) && trimmed ? (
        <div className="mb-4">
          <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Package className="h-4 w-4" aria-hidden />
            <span>Tra cứu trong game</span>
            {isGameLoading ? <span className="font-normal normal-case text-muted-foreground/80">đang tải…</span> : null}
          </div>
          <div className="mt-1 space-y-0.5">
            {isGameLoading && gameResults.length === 0 ? (
              <div className="space-y-1 px-2 py-1">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
                ))}
              </div>
            ) : null}
            {gameResults.map((item) => (
              <button
                key={item.id}
                type="button"
                className="flex w-full items-start gap-3 rounded-md p-2.5 text-left transition-colors hover:bg-muted"
                onClick={() => {
                  onViewGameSearch?.();
                  onNavigate();
                }}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-amber-500/15 p-0.5 text-amber-700 dark:text-amber-400">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" width={32} height={32} className="h-full w-full object-contain" loading="lazy" />
                  ) : (
                    <Package className="h-4 w-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium leading-snug text-foreground line-clamp-2">{item.title}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className="text-[10px] font-normal">
                      {GAME_SEARCH_CATEGORY_LABELS[item.category]}
                    </Badge>
                    {item.subtitle ? (
                      <span className="truncate text-xs text-muted-foreground">{item.subtitle}</span>
                    ) : null}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {data.wikiPosts.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <BookOpen className="h-4 w-4" aria-hidden />
            <span>Wiki & hướng dẫn</span>
          </div>
          <div className="mt-1 space-y-0.5">
            {data.wikiPosts.map((post) =>
              post.external ? (
                <a
                  key={post.id}
                  href={post.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 rounded-md p-2.5 transition-colors hover:bg-muted"
                  onClick={onNavigate}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                    <BookOpen className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium leading-snug text-foreground line-clamp-2">{post.title}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px] font-normal">
                        {post.tag}
                      </Badge>
                      <Badge variant="secondary" className="text-[10px] font-normal">
                        {post.subtag}
                      </Badge>
                    </div>
                  </div>
                </a>
              ) : (
                <Link
                  key={post.id}
                  href={post.href}
                  className="flex items-start gap-3 rounded-md p-2.5 transition-colors hover:bg-muted"
                  onClick={onNavigate}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                    <BookOpen className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium leading-snug text-foreground line-clamp-2">{post.title}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px] font-normal">
                        {post.tag}
                      </Badge>
                      <Badge variant="secondary" className="text-[10px] font-normal">
                        {post.subtag}
                      </Badge>
                    </div>
                  </div>
                </Link>
              )
            )}
          </div>
        </div>
      )}

      {data.siteCategories.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Layers className="h-4 w-4" aria-hidden />
            <span>Chức năng web</span>
          </div>
          <div className="mt-1 space-y-0.5">
            {data.siteCategories.map((cat) => (
              <Link
                key={cat.id}
                href={cat.href}
                className="flex items-center gap-3 rounded-md p-2.5 transition-colors hover:bg-muted"
                onClick={onNavigate}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-sky-500/15 text-sky-700 dark:text-sky-400">
                  <Layers className="h-4 w-4" />
                </div>
                <span className="min-w-0 flex-1 font-medium leading-snug text-foreground">{cat.name}</span>
                <Badge variant="outline" className="shrink-0 text-[10px] font-normal">
                  {cat.badge}
                </Badge>
              </Link>
            ))}
          </div>
        </div>
      )}

      {data.highlights.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Sparkles className="h-4 w-4" aria-hidden />
            <span>Gợi ý & cộng đồng</span>
          </div>
          <div className="mt-1 space-y-0.5">
            {data.highlights.map((h) =>
              h.external ? (
                <a
                  key={h.id}
                  href={h.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-md p-2.5 transition-colors hover:bg-muted"
                  onClick={onNavigate}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-violet-500/15 text-violet-700 dark:text-violet-400">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <span className="min-w-0 flex-1 font-medium leading-snug text-foreground">{h.name}</span>
                  <Badge variant="outline" className="shrink-0 text-[10px] font-normal">
                    {h.badge}
                  </Badge>
                </a>
              ) : (
                <Link
                  key={h.id}
                  href={h.href}
                  className="flex items-center gap-3 rounded-md p-2.5 transition-colors hover:bg-muted"
                  onClick={onNavigate}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-violet-500/15 text-violet-700 dark:text-violet-400">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <span className="min-w-0 flex-1 font-medium leading-snug text-foreground">{h.name}</span>
                  <Badge variant="outline" className="shrink-0 text-[10px] font-normal">
                    {h.badge}
                  </Badge>
                </Link>
              )
            )}
          </div>
        </div>
      )}

      {data.pages.length > 0 && (
        <div className="mb-2">
          <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <FileText className="h-4 w-4" aria-hidden />
            <span>Trang</span>
          </div>
          <div className="mt-1 space-y-0.5">
            {data.pages.map((page) => (
              <Link
                key={page.id}
                href={page.path}
                className="flex items-center gap-3 rounded-md p-2.5 transition-colors hover:bg-muted"
                onClick={onNavigate}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-800 dark:text-emerald-400">
                  <FileText className="h-4 w-4" />
                </div>
                <span className="font-medium text-foreground">{page.title}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {showGameFooter ? gameFooter : null}
    </div>
  );
}
